import { AxiosResponse } from "axios";
import { Client, mapUrl } from "../client";
import { Endpoint } from "../endpoint";
import { Controller } from "../controller";
import {
	MessagingAcceptTransferRequest,
	MessagingRejectTransferRequest,
} from "@/common/models/messagingTransfer";
import { InitiateTransferRequest } from "@/common/types/transfer";
import { TransferDestination } from "@/components/organisms/interactionTransferToIvr/types";
import { TransferStartResponse } from "@/common/types";
import { InfiniteQueryFnParams } from "@/hooks/infiniteQuery";
import { ExtendedPagedResult } from "../pagedResult";
import {
	CreateWhatsAppMessage,
	UploadWhatsAppMedia,
	WhatsAppMessage,
} from "@/components/molecules/interactionWorkspace";
import { AssignInteractionRequest } from "../models/assignInteraction";

export const WhatsAppMessagesKey = "WhatsAppMessages";
export const WhatsAppHistoryMessagesKey = "WhatsAppHistoryMessages";

export const acceptWhatsAppConversation = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.WhatsApp, conversationId, Endpoint.Accept);

	return Client.instance.post(url);
};

export const acceptWhatsAppConversationTransfer = (
	data: MessagingAcceptTransferRequest
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.WhatsApp, Endpoint.MessagingTransferAccept);

	return Client.instance.post(url, data);
};

export const rejectWhatsAppConversation = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.WhatsApp, conversationId, Endpoint.Reject);

	return Client.instance.post(url);
};

export const rejectWhatsAppConversationTransfer = (
	data: MessagingRejectTransferRequest
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.WhatsApp, Endpoint.MessagingTransferReject);

	return Client.instance.post(url, data);
};

export const pickUpWhatsApp = (id: string): Promise<any> => {
	const url = mapUrl(Controller.WhatsApp, Endpoint.PickUp);

	return Client.instance.post(url, id);
};

export const assignWhatsApp = (
	data: AssignInteractionRequest
): Promise<any> => {
	const url = mapUrl(Controller.WhatsApp, Endpoint.Assign);

	return Client.instance.post(url, data);
};

export const markLastWhatsAppAsSeen = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(
		Controller.WhatsApp,
		conversationId,
		Endpoint.MarkLastMessageAsSeen
	);

	return Client.instance.post(url);
};

export const closeWhatsAppConversation = (
	conversationId: string
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.WhatsApp, conversationId, Endpoint.Close);

	return Client.instance.post(url);
};

export const getWhatsAppSummary = (conversationId: string): Promise<string> => {
	return Client.instance.get<string>(
		mapUrl(Controller.WhatsApp, conversationId, Endpoint.Summary)
	);
};

export const fetchWhatsAppMessages = ({
	page: page,
	searchValue,
	sessionId,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<WhatsAppMessage>> => {
	const query = {
		page,
		pageSize: 10,
		searchValue,
	};

	const url = mapUrl(Controller.WhatsApp, sessionId!, Endpoint.Messages);

	return Client.instance.get(url, query);
};

export const fetchWhatsAppHistoryMessages = ({
	page: page,
	searchValue,
	sessionId,
}: InfiniteQueryFnParams): Promise<ExtendedPagedResult<WhatsAppMessage>> => {
	const query = {
		page: page,
		pageSize: 10,
		searchValue,
	};

	const url = mapUrl(
		Controller.WhatsApp,
		"history",
		sessionId!,
		Endpoint.Messages
	);

	return Client.instance.get(url, query);
};

export const createWhatsAppMessages = (
	sessionId: string,
	messages: CreateWhatsAppMessage[]
): Promise<AxiosResponse> => {
	const url = mapUrl(Controller.WhatsApp, sessionId);
	return Client.instance.post(url, messages);
};

export const uploadWhatsAppMedia = (
	sessionId: string,
	messageId: string,
	message: UploadWhatsAppMedia
): Promise<AxiosResponse> => {
	const url = mapUrl(
		Controller.Media,
		Endpoint.WhatsApp,
		sessionId,
		Endpoint.Messages,
		messageId
	);

	let formData = new FormData();
	formData.append("file", message.file);
	formData.append("caption", message.caption ?? "");
	formData.append("type", message.type.toString());

	return Client.instance.postFormData(url, formData);
};

export const getWhatsAppTransferDestinations = (): Promise<
	TransferDestination[]
> => {
	const url = mapUrl(
		Controller.WhatsApp,
		Endpoint.MessagingTransferDestinations
	);

	return Client.instance.get(url);
};

export const transferWhatsApp = (
	data: InitiateTransferRequest
): Promise<TransferStartResponse> => {
	const url = mapUrl(Controller.WhatsApp, Endpoint.MessagingTransfer);

	return Client.instance.post(url, data);
};

export const transferToQueueWhatsApp = (
	data: InitiateTransferRequest
): Promise<TransferStartResponse> => {
	const url = mapUrl(Controller.WhatsApp, Endpoint.MessagingTransferToQueue);

	return Client.instance.post(url, data);
};

export const parkWhatsApp = (conversationId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.WhatsApp, conversationId, Endpoint.Park)
	);
};

export const unParkWhatsApp = (conversationId: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.WhatsApp, conversationId, Endpoint.UnPark)
	);
};
